import FipeAerodynamicsAccordion from "./components/AerodynamicsAccordion";
import FipeAutonomyAccordion from "./components/AutonomyAccordion";
import FipeBrakesAccordion from "./components/BrakesAccordion";
import FipeConsumptionAccordion from "./components/ConsumptionAccordion";
import FipeDimensionsAccordion from "./components/DimensionsAccordion";
import FipeDirectionAccordion from "./components/DirectionAccordion";
import FipeEngineAccordion from "./components/EngineAccordion";
import FipeEquipmentsAccordion from "./components/EquipmentsAccordion";
import FipeMainDataAccordion from "./components/MainDataAccordion"
import FipePerformanceAccordion from "./components/PerformanceAccordion";
import FipeSuspensionAccordion from "./components/SuspensionAccordion";
import FipeTechnicalSpecificationsAccordion from "./components/TechnicalSpecificationsAccordion";
import FipeTiresAccordion from "./components/TiresAccordion";
import FipeTransmissionAccordion from "./components/TransmissionAccordion";
import FipeAdditionalsEquipmentsAccordion from "./components/AdditionalEquipmentsAccordion";
import useEquipmentModelLatestFipe from "@/features/equipments/hooks/useEquipmentModelLatestFipe";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import useEquipmentModelFipePrices from "@/features/equipments/hooks/useEquipmentModelFipePrices";
import { Mapper } from "@/common/services/mapper";
import useUserPermissions from "@/common/hooks/useUserPermissions";
import { FipeDataTransformer } from "@/features/equipments/services/fipeDataTransformer";
import { FipeDataSkeleton } from "@/features/equipments/components/show/fipeData/components/FipeDataSkeleton";

interface FipeDataProps {
  id?: string;
}

export const FipeData = ({ id }: FipeDataProps) => {
  const { 
    data: fipeData,
    refetch: refetchFipeData,
    isLoading: fipeDataIsLoading,
    error: fipeDataError = {} as any
  } = useEquipmentModelLatestFipe(String(id));

  const { 
    data: fipePricesData,
    isLoading: fipePricesDataIsLoading,
    error: fipePricesDataError = {} as any
  } = useEquipmentModelFipePrices({
    id: String(id),
    mapper: Mapper.mapWithTransformer({ transformer: FipeDataTransformer.chartTransformer() }),
  });

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions()

  const userHasPermissionToListFipePrices =
    isUserPermissionsPending || hasUserPermission('Client::EquipmentModels::FipePricesFilter')

  const fipeRawData = fipeData?.raw?.Fichas?.[0];

  if (fipeDataIsLoading) {
    return <FipeDataSkeleton />
  }

  if(fipeDataError?.status === 404) {
    return (
      <div className='flex flex-col items-center justify-center overflow-hidden h-[calc(100vh-128px)]'>
        <img src="/search.gif" className="w-full shrink-0 -mt-8 max-w-[400px]" />
        <div className="-mt-16 mb-16 p-8 space-y-2">
          <p className="text-sm font-bold text-center">Ops! Não conseguimos localizar os dados FIPE deste equipamento.</p>
          <p className="text-xs text-center">Caso acredite que seja um erro, entre em contato com o suporte.</p>
        </div>
      </div>
    )
  }

  return (
    <ErrorMessageWrapper
      className="h-[calc(100vh-128px)]"
      statusCode={fipeDataError?.status}
      refetchFunction={refetchFipeData}
    >
      <div className="flex w-full flex-col flex-1 overflow-auto max-h-[calc(100vh-128px)] gap-y-6 bg-[#FFFFFF0D] rounded-sm p-6">
        <FipeMainDataAccordion 
          fipeData={fipeData} 
          fipePricesData={fipePricesData} 
          userHasPermissionToListFipePrices={userHasPermissionToListFipePrices}
          fipePricesDataError={fipePricesDataError}
          fipePricesDataIsLoading={fipePricesDataIsLoading}
        />
        <FipeTechnicalSpecificationsAccordion specsData={fipeRawData} />
        <FipeEngineAccordion engineData={fipeRawData?.Motor} />
        <FipeTransmissionAccordion transmissionData={fipeRawData?.Transmissao} />
        <FipeSuspensionAccordion suspensionData={fipeRawData?.Suspensao} />
        <FipeBrakesAccordion brakesData={fipeRawData?.Freios} />
        <FipeDirectionAccordion steeringData={fipeRawData?.Direção} />
        <FipeTiresAccordion tiresData={fipeRawData?.Pneus} />
        <FipeDimensionsAccordion dimensionsData={fipeRawData?.Dimensoes} />
        <FipeAerodynamicsAccordion aerodynamicsData={fipeRawData?.Aerodinamica} />
        <FipePerformanceAccordion performanceData={fipeRawData?.Desempenho} />
        <FipeConsumptionAccordion consumptionData={fipeRawData?.Consumo} />
        <FipeAutonomyAccordion autonomyData={fipeRawData?.Autonomia} />
        <FipeEquipmentsAccordion equipments={fipeRawData?.Equipamentos} />
        <FipeAdditionalsEquipmentsAccordion additionalEquipments={fipeRawData?.["Equipamentos Opcionais"]} />
      </div>
    </ErrorMessageWrapper>
  );
};